/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './medication-details.reducer';

export const MedicationDetails = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const medicationDetailsList = useAppSelector(state => state.medicationDetails.entities);
  const loading = useAppSelector(state => state.medicationDetails.loading);
  const totalItems = useAppSelector(state => state.medicationDetails.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="medication-details-heading" data-cy="MedicationDetailsHeading">
        <Translate contentKey="tinyMedicRestApp.medicationDetails.home.title">Medication Details</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.medicationDetails.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/medication-details/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.medicationDetails.home.createLabel">Create new Medication Details</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.medicationDetails.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {medicationDetailsList && medicationDetailsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('repetition')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.repetition">Repetition</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('repetition')} />
                </th>
                <th className="hand" onClick={sort('tmEnd')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.tmEnd">Tm End</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tmEnd')} />
                </th>
                <th className="hand" onClick={sort('currentSupply')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.currentSupply">Current Supply</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('currentSupply')} />
                </th>
                <th className="hand" onClick={sort('refillSize')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.refillSize">Refill Size</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refillSize')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('prescribedCount')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.prescribedCount">Prescribed Count</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('prescribedCount')} />
                </th>
                <th className="hand" onClick={sort('indication')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.indication">Indication</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('indication')} />
                </th>
                <th className="hand" onClick={sort('daysFrequency')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.daysFrequency">Days Frequency</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('daysFrequency')} />
                </th>
                <th className="hand" onClick={sort('endDate')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.endDate">End Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('endDate')} />
                </th>
                <th className="hand" onClick={sort('dose')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.dose">Dose</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dose')} />
                </th>
                <th className="hand" onClick={sort('route')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.route">Route</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('route')} />
                </th>
                <th className="hand" onClick={sort('stopped')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.stopped">Stopped</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stopped')} />
                </th>
                <th className="hand" onClick={sort('stoppedBy')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.stoppedBy">Stopped By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stoppedBy')} />
                </th>
                <th className="hand" onClick={sort('patientType')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.patientType">Patient Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientType')} />
                </th>
                <th className="hand" onClick={sort('exposed')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.exposed">Exposed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('exposed')} />
                </th>
                <th className="hand" onClick={sort('medicineName')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.medicineName">Medicine Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('medicineName')} />
                </th>
                <th className="hand" onClick={sort('doseInstructions')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.doseInstructions">Dose Instructions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('doseInstructions')} />
                </th>
                <th className="hand" onClick={sort('tmType')}>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.tmType">Tm Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tmType')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.glucoUser">Gluco User</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.medicine">Medicine</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.prescription">Prescription</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicationDetails.servedBy">Served By</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {medicationDetailsList.map((medicationDetails, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/medication-details/${medicationDetails.id}`} color="link" size="sm">
                      {medicationDetails.id}
                    </Button>
                  </td>
                  <td>{medicationDetails.repetition}</td>
                  <td>{medicationDetails.tmEnd}</td>
                  <td>{medicationDetails.currentSupply}</td>
                  <td>{medicationDetails.refillSize}</td>
                  <td>{medicationDetails.timestampentry}</td>
                  <td>{medicationDetails.active ? 'true' : 'false'}</td>
                  <td>{medicationDetails.lastupdated}</td>
                  <td>{medicationDetails.cursor}</td>
                  <td>{medicationDetails.microSec}</td>
                  <td>{medicationDetails.prescribedCount}</td>
                  <td>{medicationDetails.indication}</td>
                  <td>{medicationDetails.daysFrequency}</td>
                  <td>{medicationDetails.endDate}</td>
                  <td>{medicationDetails.dose}</td>
                  <td>{medicationDetails.route}</td>
                  <td>{medicationDetails.stopped ? 'true' : 'false'}</td>
                  <td>{medicationDetails.stoppedBy}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${medicationDetails.patientType}`} />
                  </td>
                  <td>{medicationDetails.exposed ? 'true' : 'false'}</td>
                  <td>{medicationDetails.medicineName}</td>
                  <td>{medicationDetails.doseInstructions}</td>
                  <td>{medicationDetails.tmType}</td>
                  <td>
                    {medicationDetails.glucoUser ? (
                      <Link to={`/gluco-user/${medicationDetails.glucoUser.id}`}>{medicationDetails.glucoUser.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicationDetails.medicine ? (
                      <Link to={`/medicine/${medicationDetails.medicine.id}`}>{medicationDetails.medicine.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicationDetails.prescription ? (
                      <Link to={`/prescription/${medicationDetails.prescription.id}`}>{medicationDetails.prescription.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicationDetails.servedBy ? (
                      <Link to={`/pharmacy/${medicationDetails.servedBy.id}`}>{medicationDetails.servedBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/medication-details/${medicationDetails.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/medication-details/${medicationDetails.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/medication-details/${medicationDetails.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.medicationDetails.home.notFound">No Medication Details found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={medicationDetailsList && medicationDetailsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default MedicationDetails;
